import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  EmailValidator,
  M1AuthLayoutModule,
  M1ButtonModule,
  M1DisclaimerComponent,
  M1DisclaimerModule,
  M1InputModule,
  M1PasswordChecklistModule,
} from '@m1/components';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { RegisterAWSErrorCode, RegisterAWSRequest } from './core/openapi';
import { DisclaimerService } from './core/services/disclaimer.service';
import { RegisterService } from './core/services/register.service';
import {
  getPasswordErrorMessages,
  PasswordValidators,
  PasswordValidatorsStatic,
} from './core/validators/password-validator';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    M1InputModule,
    M1PasswordChecklistModule,
    M1ButtonModule,
    M1AuthLayoutModule,
    M1DisclaimerModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent {
  private readonly destroy$ = new Subject<void>();
  private translate = inject(TranslateService);
  fb = inject(FormBuilder);
  disclaimerService = inject(DisclaimerService);
  registerService = inject(RegisterService);
  activatedRoute = inject(ActivatedRoute);

  m1Disclaimer = viewChild(M1DisclaimerComponent);

  communicativeImg = './assets/logos/Simbolo_CB_1024.png';
  currentYear = new Date().getFullYear();

  Labels = signal<any>({});
  copyrightLabel = signal<string>('');

  passwordErrorMessages: { [key: string]: string } = {};
  readonly = false;
  isFormEnable = true;
  marketplaceToken: string = '';
  isloading = false;

  registerForm!: FormGroup;
  username: FormControl = new FormControl<string>('', {
    validators: Validators.required,
    updateOn: 'blur',
  });
  email: FormControl = new FormControl<string>('', {
    validators: [Validators.required, EmailValidator],
    updateOn: 'blur',
  });
  passwordControl: FormControl = new FormControl<string>('', {
    validators: [Validators.required],
    updateOn: 'change',
  });
  confirmPasswordControl: FormControl = new FormControl<string>('', {
    validators: Validators.required,
    updateOn: 'change',
  });

  constructor() {
    this.initLanguage();
  }

  private initLanguage() {
    this.translate.setDefaultLang('en');
    const storedLang = 'en';

    this.translate.use(storedLang).subscribe(() => {
      this.translate.get('APP').subscribe((translations) => {
        this.Labels.set({
          title: translations.TITLE,
          description: translations.DESCRIPTION,
          support: translations.SUPPORT,
          copyright: translations.COPYRIGHT,
        });

        this.copyrightLabel.set(
          translations.COPYRIGHT.replace('%s', this.currentYear.toString())
        );
      });
      this.translate.get('REGISTER').subscribe((translations) => {
        this.Labels.update((labels) => ({
          ...labels,
          username: translations.USERNAME,
          email: translations.EMAIL,
          password: translations.PASSWORD,
          confirmPassword: translations.CONFIRM_PASSWORD,
          button: translations.BUTTON,
          passwordCheckList: translations.PASSWORD_CHECKLIST_DESCRIPTION,
        }));
      });
      getPasswordErrorMessages(this.translate).then((messages) => {
        this.passwordErrorMessages = messages;
      });
    });
  }

  ngOnInit() {
    this.initializeForm();

    this.disclaimerService.disclaimer$
      .pipe(takeUntil(this.destroy$))
      .subscribe((disclaimer) => {
        const disclaimerComp = this.m1Disclaimer();
        if (disclaimerComp) {
          disclaimerComp.setDisclaimer(disclaimer);
        }
      });

    this.activatedRoute.queryParamMap.subscribe((params) => {
      const rawToken = params.get('x-amzn-marketplace-token');
      if (rawToken) {
        this.marketplaceToken = decodeURIComponent(rawToken);
      }
    });

    // Enable/disable password field based on email input
    this.email.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        value?.length
          ? this.passwordControl.enable()
          : this.passwordControl.disable();
      });

    // Enable/disable confirm password field when form status changes
    this.registerForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const allFieldsFilled =
          this.username.value?.length &&
          this.email.value?.length &&
          this.passwordControl.value?.length &&
          !this.registerForm.errors;

        allFieldsFilled
          ? this.confirmPasswordControl.enable()
          : this.confirmPasswordControl.disable();
      });
  }

  private initializeForm(): void {
    this.passwordControl.disable();
    this.confirmPasswordControl.disable();
    this.registerForm = this.fb.group(
      {
        password: this.passwordControl,
      },
      { validators: PasswordValidators, maxLength: 256 }
    );
  }

  onConfirmPasswordBlur(): void {
    if (!this.confirmPasswordControl.hasError('required')) {
      this.confirmPasswordControl.setValidators([
        Validators.required,
        PasswordValidatorsStatic.PasswordMatcher(this.passwordControl),
      ]);
      this.confirmPasswordControl.updateValueAndValidity();
    }
  }

  get isFormValid(): boolean {
    return (
      this.passwordControl.valid &&
      this.confirmPasswordControl.dirty &&
      this.confirmPasswordControl.valid &&
      this.isFormEnable
    );
  }

  register(): void {
    this.onConfirmPasswordBlur();

    if (this.registerForm.valid) {
      this.readonly = true;
      this.isloading = true;
      const registerAWSRequest: RegisterAWSRequest = {
        Name: this.username.value,
        Email: this.email.value,
        Password: this.passwordControl.value,
        AWSToken: this.marketplaceToken,
      };

      this.registerService
        .registerAWS(registerAWSRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isloading = false;
            this.readonly = true;

            this.disclaimerService.sendTranslatedDisclaimer(
              'success',
              'DISCLAIMER.SUCCESS'
            );
          },
          error: (error: any) => {
            this.readonly = false;
            this.isloading = false;

            // Ensure error structure is correct and extract first error
            const errorObj = Array.isArray(error.error)
              ? error.error[0]
              : error.error;

            if (errorObj?.Code) {
              const disclaimerKey = this.mapErrorToDisclaimer(errorObj.Code);
              this.disclaimerService.sendTranslatedDisclaimer(
                'alert',
                disclaimerKey
              );
            } else {
              // Fallback error message
              this.disclaimerService.sendTranslatedDisclaimer(
                'alert',
                'DISCLAIMER.ERROR'
              );
            }
          },
        });
    }
  }

  private mapErrorToDisclaimer(errorCode: RegisterAWSErrorCode): string {
    const errorMap: { [key in RegisterAWSErrorCode]: string } = {
      [RegisterAWSErrorCode.InvalidEmail]: 'DISCLAIMER.INVALID_EMAIL',
      [RegisterAWSErrorCode.InvalidPassword]: 'DISCLAIMER.INVALID_PASSWORD',
      [RegisterAWSErrorCode.AwsUserAlreadyRegistered]:
        'DISCLAIMER.AWS_USER_ALREADY_REGISTERED',
      [RegisterAWSErrorCode.EmailAlreadyRegistered]:
        'DISCLAIMER.EMAIL_ALREADY_REGISTERED',
      [RegisterAWSErrorCode.InvalidAwsToken]: 'DISCLAIMER.INVALID_AWS_TOKEN',
    };

    return errorMap[errorCode] || 'DISCLAIMER.ERROR'; // Default fallback
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
